import React, { useMemo, useRef } from 'react'

import { CanvasRendererProvider } from '@sketch-hq/sketch-web-renderer'
import {
  FrameGroupDetailsFragment,
  GetFrameViewQuery,
  useGetFrameViewQuery,
  VersionFragment,
} from '@sketch/gql-types'
import { RouteParams, useQueryRetry } from '@sketch/modules-common'
import { RouteComponentProps } from 'react-router-dom'

import { AnnotationQueryVariablesProvider } from '../../../../annotations/context'
import { DocumentSidebarLayoutExtraProps } from '../../../components/DocumentSidebarLayout'
import { Pagination } from '../../../types'
import {
  isUpgradeToLatestNeeded,
  UpgradeToLatestVersion,
  useVersioning,
} from '../../../../versioning'

import { FrameDetailErrorBoundary } from '../../components/FrameDetailErrorBoundary'
import { FrameInspectorProvider } from '../../components/FrameInspectorProvider/FrameInspectorProvider'
import { FrameView } from '../FrameView'
import { ArtboardAnnotationsOverlayContext } from '../../components/ArtboardAnnotationsOverlayContext'
import { INSPECTOR_SEGMENT } from '../../../constants'
import { GridsAndLayoutsProvider } from '../../../components/GridsAndLayoutsProvider'

const getFrame = (result: { data?: GetFrameViewQuery }) => {
  const frame = result?.data?.frame
  // at some point Apollo (while loading) returns a frame which contains only
  // a symbol of id, all other properties are non existing
  // this gives a false impression, that the object is there, but it isn't
  return frame?.identifier ? frame : undefined
}

interface UseRetryGetFrameQueryProps {
  frameUUID: string
  versionShortId?: string
  shareIdentifier: string
  enabled: boolean
}

const useRetryGetFrameViewQuery = ({
  frameUUID,
  versionShortId,
  shareIdentifier,
  enabled = true,
}: UseRetryGetFrameQueryProps) =>
  useQueryRetry(
    useGetFrameViewQuery({
      shouldInvalidatePrevious: (prev, curr) =>
        prev?.frameUUID !== curr?.frameUUID,
      // To account for frame updates we're setting a cache-and-network
      // policy for frame queries without an explicit version.
      fetchPolicy: versionShortId ? 'cache-first' : 'cache-and-network',
      variables: {
        frameUUID: frameUUID,
        documentVersionShortId: versionShortId,
        shareIdentifier,
      },
      skip: !enabled,
    }),
    {
      retryIf: result => {
        if (!enabled) return false
        const frame = getFrame(result)
        const files = (frame && frame.files) || []
        return files.some(file => !file?.url)
      },
    }
  )

const useGetPagination = (
  version: VersionFragment | undefined,
  frame: FrameGroupDetailsFragment | undefined
): Pagination | undefined => {
  const cachedPage = useRef(0)

  // Pagination is saved in a reference to avoid the pagination controls from disappearing
  // when the user navigates between pages due to loading and recalculating the pagination
  const pagination = useRef<Pagination>({
    current: undefined,
    total: undefined,
  })

  pagination.current.total = version?.document?.artboardCount
  pagination.current.current =
    frame?.documentOrder == null ? cachedPage.current : frame.documentOrder

  // We cache the current page to avoid flickering when navigating
  cachedPage.current = pagination.current.current

  return {
    total: pagination.current.total,
    current: pagination.current.current,
  }
}

type FrameViewRouteProps = RouteComponentProps<Partial<RouteParams<'FRAME'>>>

export interface FrameQueriesProps
  extends FrameViewRouteProps,
    DocumentSidebarLayoutExtraProps {}

export const FrameQueries = ({
  match,
  history,
  location,
  ...layoutProps
}: FrameQueriesProps) => {
  const { frameUUID } = match.params

  const isInspectPanelOpen = location.hash.includes(INSPECTOR_SEGMENT)

  const versionContext = useVersioning()

  const { versionShortId, share, isViewingLatestVersion } = versionContext

  const frameAtDocResult = useRetryGetFrameViewQuery({
    frameUUID: frameUUID!,
    versionShortId,
    shareIdentifier: share.identifier,
    enabled: true,
  })

  const frameAtLatestResult = useRetryGetFrameViewQuery({
    frameUUID: frameUUID!,
    /*
     * passing latestVersionId does not work
     * as on the latest version of the document the frame might be deleted
     * leaving this comment here, so that if anyone would
     * try to optimise it in the future, they would be aware
     * more: https://github.com/sketch-hq/cloud-frontend/pull/800#discussion_r289356950
     */
    // versionId={latestVersionId}
    shareIdentifier: share.identifier,
    enabled: true,
  })

  const error = frameAtDocResult.error || frameAtLatestResult.error

  const loading = frameAtDocResult.loading || frameAtLatestResult.loading

  const frames = {
    docVersion: getFrame(frameAtDocResult),
    latest: getFrame(frameAtLatestResult),
  }

  const subjects = useMemo(() => {
    if (frames.docVersion && frames.docVersion.page?.uuid) {
      return [
        {
          type: 'PAGE' as const,
          permanentId: frames.docVersion.page.uuid,
        },
      ]
    }

    return []
  }, [frames.docVersion])

  const { currentVersion } = (!versionContext.loading && versionContext) || {}
  const pagination = useGetPagination(currentVersion, frames.docVersion)

  if (isUpgradeToLatestNeeded(error)) {
    frameAtLatestResult.refetch()
    return <UpgradeToLatestVersion error={error} />
  }

  const frameAtLatestVersion = frames.latest
  const disableAnnotations = !frames.docVersion || !share.commentsEnabled

  return (
    <FrameDetailErrorBoundary>
      <CanvasRendererProvider>
        <FrameInspectorProvider
          //  Reset context for each frame
          key={frames.docVersion?.identifier}
        >
          <AnnotationQueryVariablesProvider
            shareIdentifier={share.identifier}
            subjects={subjects}
          >
            <ArtboardAnnotationsOverlayContext
              disabled={disableAnnotations}
              hidden={isInspectPanelOpen}
              permanentPageId={frames.docVersion?.page?.uuid || ''}
              isViewingLatestVersion={isViewingLatestVersion}
            >
              <GridsAndLayoutsProvider>
                <FrameView
                  share={share}
                  currentVersion={currentVersion}
                  frame={frames.docVersion!}
                  isViewingLatestVersion={isViewingLatestVersion}
                  isPrototypePlayEnabled={isViewingLatestVersion}
                  frameAtLatestVersion={frameAtLatestVersion}
                  pagination={pagination}
                  error={error}
                  loading={loading}
                  layoutProps={layoutProps}
                />
              </GridsAndLayoutsProvider>
            </ArtboardAnnotationsOverlayContext>
          </AnnotationQueryVariablesProvider>
        </FrameInspectorProvider>
      </CanvasRendererProvider>
    </FrameDetailErrorBoundary>
  )
}
